import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Walkthrough } from "../components/walkthrough/Walkthrough";
import { Paywall } from "../components/Paywall";
import { PaymentPanel } from "../components/PaymentPanel";
import { InvestigationEntry } from "../components/InvestigationEntry";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ensureSession, http, IMAGES, TOUR_KEY, CONSENT_KEY } from "../lib/api";
import { AgreeGate } from "../components/legal/AgreeGate";

const ReaShowcase = ({ art, quote }) => (
  <aside data-testid="rea-showcase" className="relative hidden lg:block">
    <div className="sticky top-24">
      <div className="relative overflow-hidden border border-[#1f1f1f]">
        <img key={art} src={art} alt="R.E.A. — Reverse Engineering Analysis entity" className="anim-kenburns h-[640px] w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="rea-scanlines pointer-events-none absolute inset-0 opacity-60" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="font-gothic text-6xl leading-none text-red-600 drop-shadow-[0_0_18px_rgba(220,38,38,0.55)]">R.E.A.</p>
          <p className="rea-label mt-2">Reverse Engineering Analysis</p>
          <p className="mt-4 border-l-2 border-[#d4af37] pl-3 text-sm italic text-neutral-300">"{quote}"</p>
        </div>
        <span className="absolute right-4 top-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-red-400"><span className="anim-pulse h-2 w-2 rounded-full bg-red-600" /> online</span>
      </div>
    </div>
  </aside>
);

const ReportsSheet = ({ open, onOpenChange, items }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent data-testid="my-reports-sheet" className="border-[#262626] bg-[#0a0a0a] text-white">
      <SheetHeader><SheetTitle className="font-display text-white">My Reports</SheetTitle></SheetHeader>
      <p className="mt-1 text-xs text-neutral-500">Reports created in this browser session. Each has a private link you can bookmark.</p>
      <div className="mt-5 space-y-2">
        {items.length === 0 && <p className="text-sm text-neutral-500">No investigations yet.</p>}
        {items.map((r) => (
          <Link key={r.token} to={`/report/${r.token}`} data-testid={`report-link-${r.token}`} className="block border border-[#262626] p-3 transition-colors hover:border-red-700">
            <p className="truncate font-mono text-sm">{r.url}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{r.status} · {new Date(r.created_at).toLocaleString()}</p>
          </Link>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

export default function Home() {
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [tour, setTour] = useState(() => !localStorage.getItem(TOUR_KEY));
  const [plan, setPlan] = useState(null);
  const [buying, setBuying] = useState(false);
  const [sheet, setSheet] = useState(false);

  const load = useCallback(async () => setSession(await ensureSession()), []);
  useEffect(() => { load(); http.get("/config").then((r) => setConfig(r.data)); }, [load]);

  const closeTour = () => { localStorage.setItem(TOUR_KEY, "1"); setTour(false); };
  const [consent, setConsent] = useState(() => !!localStorage.getItem(CONSENT_KEY));
  const agree = async () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ version: "1.0", at: new Date().toISOString() }));
    setConsent(true);
    http.post("/session/consent", { version: "1.0" }).catch(() => {});
  };
  const credits = session?.credits ?? null;
  const ready = session && config;

  let main;
  let art = IMAGES.paywall;
  let quote = "Every heart has a price. Mine is paid in USDC.";
  if (!ready) main = <p className="flex items-center gap-2 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Establishing secure session…</p>;
  else if (plan) { main = <PaymentPanel plan={plan} config={config} onBack={() => setPlan(null)} onVerified={(c) => { setSession({ ...session, credits: c }); setTimeout(() => { setPlan(null); setBuying(false); }, 1600); }} />; art = IMAGES.rea; quote = "The chain does not lie. Neither do I."; }
  else if (credits > 0 && !buying) { main = <InvestigationEntry credits={credits} onBuyMore={() => setBuying(true)} />; art = IMAGES.scan; quote = "Give me a URL. I will give you its skeleton."; }
  else main = <Paywall onSelect={setPlan} />;

  return (
    <div className="relative">
      <div className="red-vignette pointer-events-none fixed inset-0" />
      <Navbar credits={credits} onTour={() => setTour(true)} onReports={() => { load(); setSheet(true); }} />
      <main className="relative z-10 mx-auto grid max-w-[1400px] gap-12 px-5 pb-24 pt-12 sm:px-8 lg:grid-cols-[1fr_420px] lg:pt-16">
        <div className="min-w-0">
          <div className="mb-8 flex items-center gap-4 lg:hidden">
            <img src={IMAGES.rea} alt="R.E.A." className="h-20 w-20 border border-red-900/60 object-cover object-top" />
            <div><p className="font-gothic text-3xl text-red-600">R.E.A.</p><p className="rea-label">Reverse Engineering Analysis</p></div>
          </div>
          {main}
        </div>
        <ReaShowcase art={art} quote={quote} />
      </main>
      <footer className="relative z-10 flex flex-col items-center gap-3 border-t border-[#161616] py-6 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        <nav className="flex gap-5">
          <Link to="/legal/terms" data-testid="footer-terms-link" className="hover:text-white">Terms</Link>
          <Link to="/legal/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy</Link>
          <Link to="/legal/data" data-testid="footer-data-link" className="hover:text-white">Data Collection</Link>
        </nav>
        <p className="text-center">Unlovable.Click featuring R.E.A. · client-side intelligence only · no private source code accessed</p>
      </footer>
      {!consent && <AgreeGate onAgree={agree} />}
      {consent && tour && <Walkthrough onClose={closeTour} />}
      <ReportsSheet open={sheet} onOpenChange={setSheet} items={session?.investigations || []} />
    </div>
  );
}
